import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { format, isAfter } from 'date-fns';
import classes from './PPOConfiguration.module.css';

const PPOConfiguration = ({ companyData, initialValues, loading, onSubmit }) => {
  const [isEditingPasses, setIsEditingPasses] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [showTopupForm, setShowTopupForm] = useState(false);
  
  // Get all PPO entries
  const ppoEntries = companyData?.ppoData || [];
  const currentSubscription = ppoEntries[0];
  
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { companyName } = useLocation().state || {};
  
  const isSubscriptionExpired = currentSubscription && 
    isAfter(new Date(), new Date(currentSubscription.ppoExpiryDate));
    
  const handleFormSubmit = async (values, { setSubmitting }) => {
    const success = await onSubmit(values);
    setSubmitting(false);
    if (success) {
      setIsEditingPasses(false);
    }
  };    

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validationSchema = Yup.object({
    ppoPassCount: Yup.number()
      .required('Number of passes is required')
      .min(1, 'At least 1 pass is required')
      .integer('Must be a whole number'),
    expiryDate: Yup.date()
      .required('Expiry date is required')
      .min(new Date(), 'Expiry date must be in the future'),
  });

  if (loading || !companyData) {
    return <div>Loading...</div>;
  }
 
  return (
    <div className={classes.container}>
      <h2>PPU Configuration for {companyName}</h2>
      
      {currentSubscription && (
        <div className={classes.addTopupContainer}>
          <button 
            className={classes.addTopupButton}
            onClick={() => setShowTopupForm(!showTopupForm)}
          >
            {showTopupForm ? 'Hide Form' : 'Add Topup'}
          </button>
        </div>
      )}


      
      {showTopupForm && currentSubscription ? (
        <Formik
          initialValues={{
            ppoPassCount: '',
            ppoNote: '',
            expiryDate: formatDateForInput(companyData?.subscriptionEndDate),
            addTopUpPasses: true
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, ...formik }) => (
            <Form className={classes.form}>
              <div className={classes.formGroup}>
                <label htmlFor="ppoPassCount">Number of Passes</label>
                <div className={classes.inputWithButton}>
                  <Field
                    type="number"
                    id="ppoPassCount"
                    name="ppoPassCount"
                    min="1"
                    className={classes.input}
                  />
                </div>
                <ErrorMessage name="ppoPassCount" component="div" className={classes.error} />
              </div>
              <div className={classes.formGroup}>
              
              </div>

              <div className={classes.formGroup}>
                <label htmlFor="expiryDate">Expiry Date</label>
                <Field
                  type="date"
                  id="expiryDate"
                  name="expiryDate"
                  min={companyData?.subscriptionEndDate}
                  className={classes.input}
                  disabled={!isSubscriptionExpired}
                />
                <ErrorMessage name="expiryDate" component="div" className={classes.error} />
                {isSubscriptionExpired && (
                  <p className={classes.expiryWarning}>
                    This subscription has expired. Please update the expiry date to renew.
                  </p>
                )}
              </div>


              <div className={classes.formGroup}>
                <label htmlFor="ppoNote">Notes</label>
                <div className={classes.notesContainer}>
                  <div className={classes.textareaWrapper}>
                    <Field
                      as="textarea"
                      id="ppoNote"
                      name="ppoNote"
                      className={classes.textarea}
                      placeholder="Enter notes about this PPU configuration"
                    />
                   
                  </div>
                  
                </div>
              </div>

              <div className={classes.buttonGroup}>
                <button
                  type="button"
                  className={classes.cancelButton}
                  onClick={() => {
                    if (isEditingPasses) {
                      setIsEditingPasses(false);
                    } else {
                      setShowTopupForm(false);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditingPasses ? 'Cancel' : 'Close'}
                </button>
             
                  <button
                    type="submit"
                    className={classes.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                
              </div>
            </Form>
          )}
        </Formik>
      ) : !currentSubscription ? (
        <div className={classes.noSubscription}>
          <p>No active PPU subscription found. Please add a new configuration first.</p>
          <button 
            className={classes.addButton}
            onClick={() => navigate(`/company/${companyId}/add-ppo-configuration`)}
          >
            Add PPU Configuration
          </button>
        </div>
      ) : null}
      
      {ppoEntries.length > 0 && (
        <div className={classes.previousSubscriptions}>
     
          
            <div className={classes.previousList}>
              <div className={classes.tableContainer}>
                <table className={classes.subscriptionTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Notes</th>
                      <th>Pass Count</th>
                      <th>Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ppoEntries?.map((entry, index) => (
                      <tr key={index}>
                        <td>{format(new Date(entry.createdAt), 'dd/MM/yyyy')}</td>
                        <td>{entry.ppoNote || ''}</td>
                        <td>{entry.lastTopup || 0}</td>
                        <td>{format(new Date(entry.ppoExpiryDate), 'dd/MM/yyyy')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          
        </div>
      )}
    </div>
  );
};

export default PPOConfiguration;
